import React, { useState } from 'react';
import { updateJob } from '../services/jobsAPI';
import { Job, JobDetail } from '../types';

interface EditPatientDetailsModalProps {
  job: Job;
  onClose: () => void;
  onSuccess: () => void;
}

const InputField: React.FC<{
  label: string;
  name: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  type?: string;
  multiline?: boolean;
}> = ({ label, name, value, onChange, type = 'text', multiline = false }) => (
  <div className="mb-4">
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    {multiline ? (
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        rows={3}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-brand-purple sm:text-sm bg-gray-50 text-gray-900"
      />
    ) : (
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-brand-purple sm:text-sm bg-gray-50 text-gray-900"
      />
    )}
  </div>
);

const EditPatientDetailsModal: React.FC<EditPatientDetailsModalProps> = ({ job, onClose, onSuccess }) => {
  const detail = job.job_details && job.job_details.length > 0 ? job.job_details[0] : null;
  
  const [initialData] = useState<Partial<JobDetail> & { salary?: string | null }>({
    id: detail?.id,
    age: detail?.age ?? job.age ?? null,
    sex: job.job_gender ?? job.sex ?? job.cust_gender ?? detail?.gender ?? detail?.sex ?? '',
    race: detail?.race ?? job.cust_race ?? job.race ?? '',
    religion: detail?.religion ?? job.cust_religion ?? '',
    nationality: detail?.nationality ?? job.cust_nationality ?? '',
    height: detail?.height ?? job.cust_height ?? job.height ?? '',
    weight: detail?.weight ?? job.cust_weight ?? job.weight ?? '',
    location: detail?.location ?? job.cust_location ?? job.location ?? '',
    health_condition: detail?.health_condition ?? job.cust_medical_condition ?? job.health_condition ?? '',
    medical_history: detail?.medical_history ?? job.cust_medical_history ?? '',
    surgical_history: detail?.surgical_history ?? job.cust_surgical_history ?? '',
    additional_info: detail?.additional_info ?? job.additional_info ?? '',
    salary: job.salary ?? '',
  });

  const [formData, setFormData] = useState<Partial<JobDetail> & { salary?: string | null }>(initialData);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['age', 'height', 'weight', 'salary'].includes(name) ? (value === '' ? null : Number(value)) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload: Partial<Job> = {};
      const jobDetailsChanges: Partial<JobDetail> = {};
      let hasRootChanges = false;
      let hasDetailChanges = false;

      if (formData.salary !== initialData.salary) {
        payload.salary = formData.salary as string;
        hasRootChanges = true;
      }

      const detailFields = ['age', 'sex', 'race', 'religion', 'nationality', 'location', 'health_condition', 'medical_history', 'surgical_history', 'additional_info', 'weight', 'height'] as const;

      detailFields.forEach(field => {
        if (formData[field] !== initialData[field]) {
          if (field === 'sex') {
            jobDetailsChanges.gender = formData.sex;
          } else {
            (jobDetailsChanges as any)[field] = formData[field];
          }
          hasDetailChanges = true;
        }
      });

      if (hasDetailChanges && detail?.id) {
        payload.job_details = [{
          id: detail.id,
          ...jobDetailsChanges
        } as JobDetail];
      }

      if (!hasRootChanges && !hasDetailChanges) {
        onClose();
        return;
      }

      await updateJob(job.id, payload);
      onSuccess();
    } catch (error) {
      console.error('Failed to update patient details:', error);
      alert('Failed to update details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 animate-fade-in overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl m-4 transform transition-all flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b flex justify-between items-center sticky top-0 bg-white z-10 rounded-t-lg">
          <h3 className="text-lg font-bold text-gray-800">Edit Care Recipient Information</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="overflow-y-auto p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Age" name="age" value={formData.age ?? ''} onChange={handleChange} type="number" />
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select
                  name="sex"
                  value={formData.sex ?? ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-brand-purple sm:text-sm bg-gray-50 text-gray-900"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <InputField label="Race" name="race" value={formData.race ?? ''} onChange={handleChange} />
              <InputField label="Religion" name="religion" value={formData.religion ?? ''} onChange={handleChange} />
              <InputField label="Nationality" name="nationality" value={formData.nationality ?? ''} onChange={handleChange} />
              <InputField label="Height (cm)" name="height" value={formData.height ?? ''} onChange={handleChange} type="number" />
              <InputField label="Weight (kg)" name="weight" value={formData.weight ?? ''} onChange={handleChange} type="number" />
              <InputField label="Location" name="location" value={formData.location ?? ''} onChange={handleChange} />
              <InputField label="Salary" name="salary" value={formData.salary ?? ''} onChange={handleChange} type="number" />
            </div>

            <div className="mt-4 space-y-4">
              <InputField label="Health Condition" name="health_condition" value={formData.health_condition ?? ''} onChange={handleChange} multiline />
              <InputField label="Medical History" name="medical_history" value={formData.medical_history ?? ''} onChange={handleChange} multiline />
              <InputField label="Surgical History" name="surgical_history" value={formData.surgical_history ?? ''} onChange={handleChange} multiline />
              <InputField label="Additional Info" name="additional_info" value={formData.additional_info ?? ''} onChange={handleChange} multiline />
            </div>
          </form>
        </div>

        <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end gap-3 sticky bottom-0 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-purple border border-transparent rounded-md shadow-sm hover:bg-brand-purple/90 disabled:bg-brand-light-purple"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPatientDetailsModal;
